// Usage: calculateFreight(postcode, length, width, height, weight, suburb)
// Data file: freightData.json
// Fuel levy from: ../data/levy.json (dfe key)

import freightData from './freightData.json';
import levy from '../data/levy.json';

// ========== CONSTANTS ==========
const FUEL_LEVY_RATE = levy.dfe; // DFE 燃油费率 from levy.json
const CUBIC_CONVERSION = 4000;   // cm³ to kg - 匹配输出表格 B5=MAX(L*W*H/4000, weight)

// ========== HELPERS ==========

function getZone(postcode, zonesMap) {
  const pc = String(postcode).trim().padStart(4, '0');
  return zonesMap[pc] || zonesMap[String(parseInt(pc, 10))] || null;
}

function getFreightRate(zone, suburb, rates) {
  if (!zone) return null;
  const zu = zone.toUpperCase().trim();
  const su = suburb ? suburb.toUpperCase().trim() : '';

  if (su) {
    const match = rates.find(
      (r) => r.to.toUpperCase() === zu && r.suburb && r.suburb.toUpperCase() === su
    );
    if (match) return match;
  }

  return rates.find((r) => r.to.toUpperCase() === zu && !r.suburb) || null;
}

function getRemoteSurcharge(postcode, remoteMap) {
  if (!postcode) return 0;
  const pc = String(postcode).trim();
  // Simple postcode lookup - if postcode exists in map, return surcharge
  const surcharge = remoteMap[pc] || remoteMap[pc.padStart(4, '0')];
  return surcharge !== undefined ? surcharge : 0;
}

/**
 * 重量档位附加费 (Weight Tier Surcharge)
 * IF(weight<=60, 7, IF(weight<=90, 14, IF(weight<=120, 21, IF(weight<=150, 28, 35))))
 */
function getWeightTierSurcharge(chargeableWeight) {
  if (chargeableWeight <= 60) return 7;
  if (chargeableWeight <= 90) return 14;
  if (chargeableWeight <= 120) return 21;
  if (chargeableWeight <= 150) return 28;
  return 35;
}

/**
 * 长度附加费 (Length Surcharge based on max dimension)
 * IF(MAX(dims)<150, 0, IF(<=199, 8, IF(<=299, 18, IF(<=399, 35, IF(<=499, 90, IF(<=599, 240, 460))))))
 */
function getLengthSurcharge(maxDimension) {
  if (maxDimension < 150) return 0;
  if (maxDimension <= 199) return 8;
  if (maxDimension <= 299) return 18;
  if (maxDimension <= 399) return 35;
  if (maxDimension <= 499) return 90;
  if (maxDimension <= 599) return 240;
  return 460;
}

/**
 * 围度附加费 (Girth Surcharge)
 * IF(SUM(dims) > 220, 8, 0)
 */
function getGirthSurcharge(length, width, height) {
  return (length + width + height) > 220 ? 8 : 0;
}

// ========== CALCULATION ==========

function calculateFreight(postcode, length, width, height, weight, suburb = '') {
  const { zones, rates, remoteList } = freightData;

  const result = {
    postcode: String(postcode).trim(),
    zone: null,
    suburb: suburb ? suburb.toUpperCase().trim() : '',
    cubicWeight: 0,
    chargeableWeight: 0,
    basicCharge: 0,
    ratePerKg: 0,
    minCharge: 0,
    calculatedCost: 0,
    fuelLevy: 0,
    weightTierSurcharge: 0,
    lengthSurcharge: 0,
    girthSurcharge: 0,
    remoteSurcharge: 0,
    totalFreight: 0,
    isSuburbRate: false,
    error: null,
  };

  // 1. Parse dimensions and weight
  const l = parseFloat(length) || 0;
  const w = parseFloat(width) || 0;
  const h = parseFloat(height) || 0;
  const aw = parseFloat(weight) || 0;

  // Calculate chargeable weight: CEILING(MAX(actual, cubic), 1)
  const cubicWeight = (l * w * h) / CUBIC_CONVERSION;
  const chargeableWeight = Math.ceil(Math.max(aw, cubicWeight));

  // 2. Postcode -> Zone
  result.zone = getZone(postcode, zones);
  if (!result.zone) {
    result.error = `No zone found for postcode ${postcode}`;
    return result;
  }

  result.cubicWeight = +cubicWeight.toFixed(5);
  result.chargeableWeight = chargeableWeight;

  // 4. Zone + Suburb -> Rate
  const rate = getFreightRate(result.zone, suburb, rates);
  if (!rate) {
    result.error = `No rate found for zone ${result.zone}${suburb ? ` / suburb ${suburb}` : ''}`;
    return result;
  }
  result.basicCharge = +rate.basicCharge.toFixed(5);
  result.ratePerKg = +rate.rate.toFixed(5);
  result.minCharge = +rate.minCharge.toFixed(5);
  result.isSuburbRate = !!rate.suburb;

  // 5. Excel: IF(E+(CEILING(weight,1)*F) > G, (E+CEIL*F)*(1+fuel), G*(1+fuel))
  //    即: MAX(basicCharge + ceil(weight)*rate, minCharge) * (1 + fuelRate)
  const weightBased = rate.basicCharge + chargeableWeight * rate.rate;
  const calculatedCost = Math.max(weightBased, rate.minCharge);
  result.calculatedCost = +calculatedCost.toFixed(5);

  // 6. 燃油附加费: calculatedCost * (1 + fuelRate)
  const costWithFuel = calculatedCost * (1 + FUEL_LEVY_RATE);
  result.fuelLevy = +(costWithFuel - calculatedCost).toFixed(5);

  // 7. Weight tier surcharge: IF(weight<=60, 7, ...)
  const weightTierSurcharge = getWeightTierSurcharge(chargeableWeight);
  result.weightTierSurcharge = weightTierSurcharge;

  // 8. Length surcharge: IF(MAX(dims)<150, 0, ...)
  const maxDimension = Math.max(l, w, h);
  const lengthSurcharge = getLengthSurcharge(maxDimension);
  result.lengthSurcharge = lengthSurcharge;

  // 9. Girth surcharge: IF(SUM(dims)>220, 8, 0)
  const girthSurcharge = getGirthSurcharge(l, w, h);
  result.girthSurcharge = girthSurcharge;

  // 10. Remote surcharge: IFERROR(VLOOKUP(postcode, remote!B:C, 2, 0), 0)
  const remoteSurcharge = getRemoteSurcharge(postcode, remoteList);
  result.remoteSurcharge = +remoteSurcharge.toFixed(5);

  // 11. Total = costWithFuel + weightTierSurcharge + lengthSurcharge + girthSurcharge + remoteSurcharge
  const totalFreight = costWithFuel + weightTierSurcharge + lengthSurcharge + girthSurcharge + remoteSurcharge;
  result.totalFreight = +totalFreight.toFixed(5);

  return result;
}

// 适配器函数，匹配其他快递公司的接口: (postcode, weight, length)
// 假设默认宽度和高度为 30cm
export const calculateShipping = (postcode, weight, length, width = 30, height = 30, suburb = '') => {
  const result = calculateFreight(postcode, length, width, height, weight, suburb);

  if (result.error) {
    return 999999;
  }

  return result.totalFreight;
};

// 导出详细计算函数和辅助函数供需要的地方使用
export { calculateFreight, getZone, getFreightRate, getRemoteSurcharge };

/*
使用说明
1. calculateShipping(postcode, weight, length, [width], [height], [suburb]) - 简化接口，返回运费总额
2. calculateFreight(postcode, length, width, height, weight, [suburb]) - 详细接口，返回完整计算信息
3. 参数说明:
   - postcode: 邮编 (必填)
   - length: 长度 (cm)
   - width: 宽度 (cm)
   - height: 高度 (cm)
   - weight: 实际重量 (kg)
   - suburb: 郊区名称 (可选，某些特殊区域需要)
4. 燃油附加费率: 从 levy.json 的 dfe 字段读取
5. 立方重量计算: (长 × 宽 × 高) ÷ 4000
6. 计费重量: CEILING(MAX(实际重量, 立方重量), 1) — 向上取整
7. 运费公式: MAX(基础运费 + 费率×计费重量, 最低收费) × (1 + 燃油费率) + 重量档位费 + 长度附加费 + 围度附加费 + 偏远附加费
8. 重量档位费: ≤60kg→$7, ≤90kg→$14, ≤120kg→$21, ≤150kg→$28, >150kg→$35
9. 长度附加费 (最长边): <150cm→$0, ≤199cm→$8, ≤299cm→$18, ≤399cm→$35, ≤499cm→$90, ≤599cm→$240, >599cm→$460
10. 围度附加费: SUM(长+宽+高) > 220cm → $8, 否则 $0
11. 偏远附加费: VLOOKUP(postcode, remote表, 2, 0)，查不到则为 0
12. 所有计算结果保留 5 位小数
*/
